import { Box, Portal } from "@chakra-ui/react";
import { ErrorScreen, Spinner } from "@components/ui-kit";
import { t } from "@lingui/macro";
import { useAccessTokenForBudgetovacQuery } from "@src/__generated__/graphql";
import { BUDGETOVAC_BASE_URL } from "@src/config/constants";
import { onError } from "@src/utils/apollo";
import { useScreenType } from "@src/utils/hooks/useIsMobile";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { urlJoinP } from "url-join-ts";
import { useWarnBeforeRouteChangeIfUnsavedWork } from "./hooks";
import type { BudgetovacQueryParams, TQueryBoolean } from "./types";

interface BudgetovacIframeProps {
  page: "budgets" | "positions" | "positions-sets" | "create";
  embedded: boolean;
  showMenu: boolean;
  proposals?: boolean;
  projectId?: string;
  clientId?: string;
  budgetId?: string;
  budgetNumber?: string;
  rateCardId?: string;
  iframeKey?: string;
}

export const BUDGETOVAC_CREATE_TEMPLATE_QUERY_KEY = "budgetTemplateId";

const BudgetovacIframe = ({
  page,
  embedded,
  showMenu,
  proposals,
  projectId,
  clientId,
  budgetId,
  rateCardId,
  budgetNumber,
  iframeKey,
}: BudgetovacIframeProps) => {
  const [iFrameLoading, setIframeLoading] = useState(true);
  const { data, loading } = useAccessTokenForBudgetovacQuery({ ...onError() });
  const { isMobile } = useScreenType();
  const router = useRouter();

  useWarnBeforeRouteChangeIfUnsavedWork();

  const queryParams: BudgetovacQueryParams = {
    access_token: data?.getAccessTokenForThirdParty || "",
    embeded: booleanToString(embedded),
    show_menu: booleanToString(showMenu),
  };

  useEffect(() => {
    window.addEventListener("message", handleIframeEvents);
    return () => window.removeEventListener("message", handleIframeEvents);
  }, []);

  if (proposals) {
    queryParams.proposals = booleanToString(proposals);
  }

  if (projectId) {
    queryParams.project_id = projectId;
  }

  if (clientId) {
    queryParams.client_id = clientId;
  }

  if (budgetNumber) {
    queryParams.budget_number = budgetNumber;
  }

  const returnUrl = router.query?.returnParams;
  if (returnUrl) {
    queryParams.returnParams = Array.isArray(returnUrl)
      ? returnUrl?.[0]
      : returnUrl;
  }

  if (
    page === "create" &&
    router.isReady &&
    BUDGETOVAC_CREATE_TEMPLATE_QUERY_KEY in router.query
  ) {
    const templateId = router.query[BUDGETOVAC_CREATE_TEMPLATE_QUERY_KEY];

    queryParams[BUDGETOVAC_CREATE_TEMPLATE_QUERY_KEY] = Array.isArray(
      templateId,
    )
      ? templateId?.[0]
      : templateId;
  }

  const budgetsUrl = urlJoinP(
    process.env.NODE_ENV !== "development"
      ? BUDGETOVAC_BASE_URL
      : "http://localhost:4200",
    [
      page === "create" ? "budgets" : page,
      page === "positions-sets" && rateCardId ? `/edit/${rateCardId}` : "",
      page === "budgets" && budgetId
        ? `/edit/${budgetId}`
        : page === "create"
          ? "/create"
          : "",
    ],
    queryParams,
  );

  const handleIframeEvents = (
    e: MessageEvent<{
      type: string;
      project_name?: string;
      budget_id?: string;
      brand_id?: string;
    }>,
  ) => {
    let allowedOrigin: string | undefined;
    try {
      allowedOrigin = new URL(BUDGETOVAC_BASE_URL).origin;
    } catch {}
    if (!allowedOrigin || allowedOrigin !== e.origin) return;
    if (e.data.type === "createProject") {
      router.push({
        pathname: "/projects/create",
        query: {
          budget_id: e.data.budget_id,
          title: e.data.project_name,
          brand_id: e.data.brand_id,
        },
      });
    }
  };

  if (loading) {
    return <Spinner absolute />;
  }

  return (
    <React.Fragment>
      {isMobile && (
        <Portal>
          <Box
            pos="fixed"
            top="52px"
            h="calc(100vh - 52px - 8rem"
            bg="rgb(240, 244, 248)"
            insetX="0"
          >
            <ErrorScreen title={t`Mobile version coming soon.`} />
          </Box>
        </Portal>
      )}
      {iFrameLoading && <Spinner absolute />}
      <iframe
        key={iframeKey}
        src={budgetsUrl}
        title="Budgetovac"
        onLoad={() => setIframeLoading(false)}
        className="w-full h-full"
        frameBorder={0}
      />
    </React.Fragment>
  );
};

const booleanToString = (val: boolean): TQueryBoolean =>
  val ? "true" : "false";

export default observer(BudgetovacIframe);
